import { EnumItemInterface } from './../../../../../view-models/base-type/enum-property-view-model';
import { AfterViewInit, ElementRef, EventEmitter, NgZone, Output, Renderer2 } from '@angular/core';
import { ChangeDetectionStrategy, OnChanges, OnDestroy, Component, Input, ViewChild, ChangeDetectorRef, SimpleChanges, output } from '@angular/core';
import { Dropdown, DropdownModule } from 'primeng/dropdown';
import { NgxPopperjsDirective, NgxPopperjsModule, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { PopperHelper } from '@nts/std/utility';
import { BehaviorSubject, filter, fromEvent, map, Subject, takeUntil, throttleTime } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { OverlayOptions } from 'primeng/api';
import { RibbonButtonComponent } from '../../../../shared/buttons/ribbon-button/ribbon-button.component';
import { ViewReadyCoordinator } from '../../view-ready-coordinator';

@UntilDestroy()
@Component({
  selector: 'nts-base-enum-text-box',
  templateUrl: './base-enum-text-box.component.html',
  styleUrls: ['./base-enum-text-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DropdownModule,
    FormsModule,
    NgxPopperjsModule,
    AsyncPipe,
    NgFor,
    NgIf,
    RibbonButtonComponent
  ]
})
export class BaseEnumTextBoxComponent implements OnChanges, OnDestroy, AfterViewInit {

  @Input() tabIndex = -1;
  @Input() isDisabled = false;
  @Input() isReadonly = false;
  @Input() value: EnumItemInterface<number | string>;
  @Input() options$: BehaviorSubject<Array<EnumItemInterface<number | string>>>;
  @Input() customClasses = '';
  @Input() errorList: string[] = [];
  @Input() showErrorTooltip = true;
  @Input() showErrorBorder = true;
  @Input() hasError = false;
  @Input() labelText: string;
  @Input() appendTo: string | HTMLDivElement = null;
  @Input() placeholder = '';
  @Input() title: string;
  @Input() customCommandList = [];
  @Input() scrollElementClass = 'layout-main scrollable-content';
  @Input() dataKey = 'key';
  @Input() optionLabel = 'description';
  @Input() optionDisabled = 'disabled';
  @Input() panelClass = 'dropdown-panel-bottom';
  @Input() isNullable = false;
  @Input() listenClickOutside = false;
  @Input() primaryColor = null;
  @Input() overlayOptions: OverlayOptions = null;

  private _openDropdownOnUpDownArrowKey: boolean = true;

  // gestisco openDropdownOnUpDownArrowKey in questo modo così scatta anche quando non si usa il binding da template,
  // ma si imposta direttamente da codice questa proprietà, altrimenti non funzionerebbe in quel caso specifico
  @Input() set openDropdownOnUpDownArrowKey(value: boolean) {
    this._openDropdownOnUpDownArrowKey = value;
    this.handleOpenDropdownOnUpDownArrowKey();
  }
  get openDropdownOnUpDownArrowKey(): boolean {
    return this._openDropdownOnUpDownArrowKey;
  }


  @Output() onFinishEditing = new EventEmitter();
  @Output() onChange = new EventEmitter();
  @Output() valueChange = new EventEmitter<EnumItemInterface<any>>();
  @Output() onBlur = new EventEmitter();
  @Output() onFocus = new EventEmitter();
  readonly viewReady = output<void>();


  isViewReady = false;
  @ViewChild('combo', { static: false }) combo: Dropdown;
  @ViewChild('popperError', { static: false }) popperError: NgxPopperjsDirective;
  @ViewChild('popperInfo', { static: false }) popperInfo: NgxPopperjsDirective;

  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  simpleOptions$: BehaviorSubject<Array<object>> = new BehaviorSubject<Array<object>>([]);
  isDropdownOpen$ = new BehaviorSubject<boolean>(false);
  parentComponent: any = null;

  #superOnArrowDownKeyImplementation: (event: KeyboardEvent) => void;
  #superOnArrowUpKeyImplementation: (event: KeyboardEvent) => void;
  #documentClickListener: any;
  #optionsDestroy$: Subject<void> = new Subject();
  #viewReadyCoordinator = new ViewReadyCoordinator(['self'] as const, () => this.emitViewReady());
  #destroyed = false;

  constructor(
    public readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly cd: ChangeDetectorRef,
  ) { }

  ngOnInit() {
    if (this.listenClickOutside) {
      this.bindDocumentClickListener();
    }
    this.updateOptions();
  }

  get input() {
    return this.combo.el.nativeElement.children[0].children[0];
  }

  focus() {
    this.input.focus();
  }

  ngAfterViewInit(): void {
    if (this.primaryColor) {
      // ng-select-container
    }

    this.#superOnArrowDownKeyImplementation = this.combo.onArrowDownKey;
    this.#superOnArrowUpKeyImplementation = this.combo.onArrowUpKey;

    this.handleOpenDropdownOnUpDownArrowKey();

    queueMicrotask(() => {
      if (this.#destroyed) {
        return;
      }
      this.#viewReadyCoordinator.markReady('self');
      this.#viewReadyCoordinator.tryEmit(() => this.combo != null);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      if (!this.isNullable && changes['value']?.currentValue == null) {
        changes['value'].currentValue = this.options$.value[0];
        this.value = changes['value'].currentValue;
        this.cd.detectChanges();
        this.valueChange.emit(this.value);
        this.onChange.emit({ value: this.value });
      }
    }

    if (changes['options$']) {
      this.updateOptions();
    }

    if (changes['errorList']) {
      this.checkPopper();
    }

    if (changes['openDropdownOnUpDownArrowKey']) {
      this.handleOpenDropdownOnUpDownArrowKey();
    }
  }

  ngOnDestroy(): void {
    this.#destroyed = true;
    this.#viewReadyCoordinator.destroy();
    this.#optionsDestroy$.next();
    this.#optionsDestroy$.complete();
    this.unbindDocumentClickListener();
  }

  handleOpenDropdownOnUpDownArrowKey() {
    if (this.combo == null) {
      return;
    }
    if (!this.openDropdownOnUpDownArrowKey) {

      // Patch for primeng to support disabling the dropdown opening with arrow key

      this.combo.onArrowDownKey = (event: KeyboardEvent) => {

        if (!this.combo.overlayVisible && event.code === 'ArrowDown') {
          return;
        }

        // Call the original implementation
        this.#superOnArrowDownKeyImplementation.call(this.combo, event);

      };


      this.combo.onArrowUpKey = (event: KeyboardEvent) => {

        if (!this.combo.overlayVisible && event.code === 'ArrowUp') {
          return;
        }

        // Call the original implementation
        this.#superOnArrowUpKeyImplementation.call(this.combo, event);

      };
    } else {
      this.combo.onArrowDownKey = (event: KeyboardEvent) => {
        // Call the original implementation
        this.#superOnArrowDownKeyImplementation.call(this.combo, event);
      };

      this.combo.onArrowUpKey = (event: KeyboardEvent) => {

        // Call the original implementation
        this.#superOnArrowUpKeyImplementation.call(this.combo, event);

      };
    }
  }

  updateOptions() {
    this.#optionsDestroy$.next();
    this.options$
      .pipe(
        takeUntil(this.#optionsDestroy$),
        untilDestroyed(this),
        map((options) => {
          return options.map((singleOption: EnumItemInterface<number | string>) => {
            return {
              [this.dataKey]: singleOption.key,
              [this.optionLabel]: singleOption.description,
              descriptionExt: singleOption.descriptionExt,
              [this.optionDisabled]: singleOption.disabled,
            };
          });
        })
      )
      .subscribe((s) => {
        this.simpleOptions$.next(s);
      });
  }

  clear() {
    this.combo.clear();
  }

  dropdownChange(e) {
    this.valueChange.emit(e.value);
    this.onChange.emit(e);
  }

  onShow($event): void {
    this.isDropdownOpen$.next(true);

    this.panelClass = $event.element.getBoundingClientRect().top < this.combo.el.nativeElement.getBoundingClientRect().top ? 'dropdown-panel-bottom' : 'dropdown-panel-top';


    fromEvent(document.getElementsByClassName(this.scrollElementClass), 'scroll').pipe(
      untilDestroyed(this),
      takeUntil(this.isDropdownOpen$.pipe(filter((isDropdownOpen) => isDropdownOpen === false))),
      throttleTime(1000)
    ).subscribe((e: Event) => {
      // this.focus(null);
      this.hide();
      this.onFinishEditing.emit();
    });

  }

  onHide(e) {
    this.isDropdownOpen$.next(false);
  }

  show() {
    this.combo.show();
  }

  hide() {
    this.combo.hide();
  }

  blur($event) {
    this.popperError?.hide();
    this.popperInfo?.hide();
    this.onBlur.emit($event);
  }

  private bindDocumentClickListener() {
    if (!this.#documentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = this.el ? this.el.nativeElement.ownerDocument : 'document';

        this.#documentClickListener = this.renderer.listen(documentTarget, 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.zone.run(() => {
              this.onFinishEditing.emit();

              this.cd.markForCheck();
            });
          }

        });
      });
    }
  }

  private unbindDocumentClickListener() {
    if (this.#documentClickListener) {
      this.#documentClickListener();
      this.#documentClickListener = null;
    }
  }

  private isOutsideClicked(event: Event) {
    return !(this.el.nativeElement.isSameNode(event.target) || this.el.nativeElement.contains(event.target) || this.isDropDownClicked(event));
  }

  private isDropDownClicked(event: Event) {
    return this.combo ? this.combo.el.nativeElement.contains(event.target) || this.combo.overlayViewChild?.el?.nativeElement?.contains(event.target as any) : false;
  }

  private checkPopper() {
    if (this.popperError && (this.errorList == null || this.errorList.length === 0)) {
      PopperHelper.hide(this.popperError);
    }
    if (this.popperInfo && this.errorList?.length > 0) {
      PopperHelper.hide(this.popperInfo);
    }
  }

  private emitViewReady(): void {
    if (this.#destroyed) {
      return;
    }
    this.isViewReady = true;
    this.viewReady.emit();
  }
}
